import React, { FunctionComponent, useState, useEffect } from 'react'
import { Button, InputGroup, Form, Card, Row, Col } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RootState from '../../../types/RootState'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { getServices } from '../../../redux/actions/providerService'
import { truncate, replaceSpecialChars } from '../../../utils/stringTools'

import './index.css'

interface Props {
  height: number;
  selectedServiceCode: string;
}

const getRate = (allDatesAreAfterToday: boolean, row: any) => {
  return allDatesAreAfterToday ? row.rates.reduce((acc, curr) => { // Return date closest to today
    if (acc) {
      if (moment().diff(moment(truncate(10, curr.effectiveDate)), 'days') < moment().diff(moment(truncate(10, acc.effectiveDate)), 'days')) {
        return acc
      } else {
        return curr
      }
    } else {
      return curr
    }
  }, null) : row.rates.reduce((acc, curr) => {
    if (acc) {
      if (moment().add('days', 1).isAfter(moment(truncate(10, curr.effectiveDate)))) {
        if (moment().diff(moment(truncate(10, curr.effectiveDate)), 'days') > moment().diff(moment(truncate(10, acc.effectiveDate)), 'days')) {
          return acc
        } else {
          return curr
        }
      } else {
        return acc
      }
    } else {
      if (moment().add('days', 1).isAfter(moment(truncate(10, curr.effectiveDate)))) {
        return curr
      } else {
        return acc
      }
    }
  }, null)
}

const  getRateValue = (foundRate: any, row: any, cellContent: any) => foundRate ?
  (foundRate.rateType === 'T19' ? ((row.t19Rate !== null && row.t19) ? `$${row.t19Rate.toFixed(2)}` : '') : foundRate.memberRate ? `$${foundRate.memberRate.toFixed(2)}` : '') : 
  (cellContent ? `$${parseFloat(cellContent).toFixed(2)}` : '')

const formatRatesForTable = (rates: any, filters: any) => {
  return rates
    .filter((rate) => { // Filter records that do not have a rate loaded (includes T19 Not Loaded & Non-T19 services that don’t have at least one rate)
      const allDatesAreAfterToday = rate.rates.every(rate => rate.effectiveDate && moment().add('days', 1).isBefore(moment(truncate(10, rate.effectiveDate))))
      const foundRate = getRate(allDatesAreAfterToday, rate)
      return !(getRateValue(foundRate, rate, rate.rate) === '')
    })
    .filter((rate) => {
      return filters.subServiceCode === '' ? rate : ((rate.subServiceCode === null ? '' : rate.subServiceCode).toLowerCase().indexOf(filters.subServiceCode.toLowerCase()) !== -1)
    })
    .filter((rate) => {
      return filters.subServiceCodeDescription === '' ? rate : ((rate.subServiceDescription === null ? '' : rate.subServiceDescription).toLowerCase().indexOf(filters.subServiceCodeDescription.toLowerCase()) !== -1)
    })
    .filter((rate) => {
      return filters.activeOnly ? !rate.obsolete : rate
    })
}

const expandRowRenderer = (row: any, services: any) => {
  const columns = [{
    dataField: 'effectiveDate',
    text: 'Effective Date',
    sort: true,
    formatter: (cellContent) => cellContent ? moment(truncate(10, cellContent)).format('MM/DD/YYYY') : ''
  }, {
    dataField: 'memberRate',
    text: 'Rate',
    sort: true,
    formatter: (cellContent, row) => {
      let rate = ''
      const findService = services.find(service => service.providerServiceId === row.providerServiceId)
      if (findService && !findService.publishedRate) return 'N/A'

      if (row.rateType === 'T19') {
        if (findService) rate = ( findService.t19Rate !== null && findService.t19 ) ? `$${findService.t19Rate.toFixed(2)}` : ''
      } else {
        if (cellContent) {
          rate = `$${parseFloat(cellContent).toFixed(2)}`
        }
      }

      const pattern = /^Z[a-zA-Z0-9]{4}$/
      const zPackageCode = pattern.test(row.code)
      if ( rate === '$1.00' && zPackageCode) {
        rate = 'Pay as Billed'
      }

      return rate
    }
  }, {
    dataField: 'rateType',
    text: 'Rate Type',
    sort: true
  }]

  return (
    <div style={{height: '100%'}}>
      <ToolkitProvider
        keyField='providerServiceRateId'
        bootstrap4
        data={row.rates}
        columns={columns}
        search
      >
        {
          (props: any) => (
            <div className='rate-table' style={{width: '100%'}}>
              <BootstrapTable
                id='rateTableChild'
                bordered={false}
                striped
                condensed
                bootstrap4
                noDataIndication={() => <div>No Rates</div>}
                { ...props.baseProps }
              />
            </div>
          )
        }
      </ToolkitProvider>
    </div>
  )
}

const expandHeaderColumnRenderer = () => <div />

const expandColumnRenderer = ({ expanded }: any) =>  expanded ? <FontAwesomeIcon icon={['fas', 'chevron-up']} className='expandedIcon' /> : <FontAwesomeIcon icon={['fas', 'chevron-down']} className='expandedIcon' />

const Rate: FunctionComponent<Props> = ({ height, selectedServiceCode }: Props) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState<Array<number>>([])

  const [subServiceRateFilters, setSubServiceRateFilters] = useState({
    subServiceCode: '',
    subServiceCodeDescription: '',
    activeOnly: false
  })

  const { services, loading, selectedProvider } = useSelector((state: RootState) => ({
    services: state.providerService.data,
    loading: state.providerService.loading,
    selectedProvider: state.authentication.selectedProvider
  }))

  const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''

  useEffect(() => {
    dispatch(getServices(selectedProvider.provider_id, token))
  }, [selectedProvider.provider_id]) // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    const foundSelectedServiceCode = services.filter(service => replaceSpecialChars(service.subServiceDescription) === selectedServiceCode)

    if (foundSelectedServiceCode.length > 0) {
      const activeSelectedServiceCode = foundSelectedServiceCode.length > 1 ? foundSelectedServiceCode.find(serviceCode => serviceCode.obsolete === false) : foundSelectedServiceCode[0]
      const selectedRow = document.getElementById(`${selectedServiceCode}_${activeSelectedServiceCode.obsolete}`)

      setExpanded([...expanded, activeSelectedServiceCode.providerServiceId])
      selectedRow?.focus()
    }

  }, [selectedServiceCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeFilter = (val: any, key: string) => {
    setSubServiceRateFilters({
      ...subServiceRateFilters,
      [key]: val
    })
  }
 
  let data = loading === false ? (<div>No Rates available</div>) : (
    <div className='organization-circular-progress'>
      <CircularProgress size={40} />
      <span>Loading data</span>
    </div>
  )

  const columns = [{
    dataField: 'subServiceCode',
    text: 'Service Code',
    sort: true,
    // eslint-disable-next-line react/display-name
    formatter: (cellContent, row, rowIndex) => <div id={`${replaceSpecialChars(row.subServiceDescription)}_${row.obsolete}`} tabIndex={rowIndex}>{cellContent}</div>
  }, {
    dataField: 'subServiceDescription',
    text: 'Service Description',
    sort: true,
    formatter: (cellContent) => cellContent
  }, {
    dataField: 'rate',
    text: 'Rate',
    sort: true,
    formatter: (cellContent, row) => {
      // Hide if publishedRate is false
      if (!row.publishedRate) return 'N/A'

      // check if all rates start after today
      const allDatesAreAfterToday = row.rates.every(rate => rate.effectiveDate && moment().add('days', 1).isBefore(moment(truncate(10, rate.effectiveDate))))
      const foundRate = getRate(allDatesAreAfterToday, row)
      const rateValue = getRateValue(foundRate, row, cellContent)
      const pattern = /^Z[a-zA-Z0-9]{4}$/
      const zPackageCode = pattern.test(row.code)
      return  rateValue === '$1.00' && zPackageCode ? 'N/A' : rateValue
    }
  }, {
    dataField: 'effectiveRateDate',
    text: 'Effective Date',
    sort: true,
    formatter: (cellContent, row) => {
      // check if all rates start after today
      const allDatesAreAfterToday = row.rates.every(rate => rate.effectiveDate && moment().add('days', 1).isBefore(moment(truncate(10, rate.effectiveDate))))
      const foundRate = getRate(allDatesAreAfterToday, row)
      return  foundRate ?  moment(truncate(10, foundRate.effectiveDate)).format('MM/DD/YYYY') : ''
    }
  }, {
    dataField: 'obsolete',
    text: 'Status',
    sort: true,
    formatter: (cellContent) => !cellContent ? 'Active' : 'Archived'
  }]

  const handleOnExpand = (row, isExpand) => {
    if (isExpand) {
      setExpanded([...expanded, row.providerServiceId])
    } else {
      setExpanded(expanded.filter(x => x !== row.providerServiceId))
    }
  }

  const expandRow = {
    onlyOneExpanding: true,
    renderer: (row) => expandRowRenderer(row, services),
    showExpandColumn: true,
    expandByColumnOnly: true,
    expandHeaderColumnRenderer: expandHeaderColumnRenderer,
    expandColumnRenderer: expandColumnRenderer,
    onExpand: handleOnExpand,
    expanded,
    className: 'expandableRow'
  }

  return (
    <div style={{ height:height-370 }}>
      <Card className='filter-container'>
        <Card.Body>
          <Row>
            <Col xs={3}>
              <Form.Label>Sub Service Code</Form.Label>
              <InputGroup className='mb-3 rate-search-filter' style={{marginBottom:0 }}>
                <InputGroup.Append>
                  <Button variant='outline-secondary' className='search-btn'><FontAwesomeIcon icon={['fas', 'search']} /></Button>
                </InputGroup.Append>
                <Form.Control 
                  type='text' 
                  placeholder='Quick Search' 
                  value={subServiceRateFilters.subServiceCode} 
                  onChange={(event) => handleChangeFilter(event.target.value, 'subServiceCode')}
                />
              </InputGroup>
            </Col>
            <Col xs={3}>
              <Form.Label>Sub Service Code Description</Form.Label>
              <InputGroup className='mb-3 rate-search-filter' style={{marginBottom:0}}>
                <InputGroup.Append>
                  <Button variant='outline-secondary' className='search-btn'><FontAwesomeIcon icon={['fas', 'search']} /></Button>
                </InputGroup.Append>
                <Form.Control 
                  type='text' 
                  placeholder='Quick Search' 
                  value={subServiceRateFilters.subServiceCodeDescription}
                  onChange={(event) => handleChangeFilter(event.target.value, 'subServiceCodeDescription')}
                />
              </InputGroup>
            </Col>
            <Col xs={6} className='rate-right-filter'>
              <div className='rate-right-filter-col'>
                <Form.Check type='checkbox' label='Show only ACTIVE rates' onChange={() => handleChangeFilter(!subServiceRateFilters.activeOnly, 'activeOnly')} />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <div style={{height: '100%'}}>
        <ToolkitProvider
          keyField='providerServiceId'
          data={services ? formatRatesForTable(services, subServiceRateFilters) : []}
          columns={columns}
          search
        >
          {
            props => (
              <div className='rate-table' style={{width: '100%'}}>
                <BootstrapTable
                  id='rateTable'
                  bordered={false}
                  striped
                  condensed
                  bootstrap4
                  noDataIndication={() => (data)}
                  expandRow={expandRow}
                  { ...props.baseProps }
                />
              </div>
            )
          }
        </ToolkitProvider>
      </div>
    </div>
  )
}

export default Rate